import { useState, useEffect } from "react";
import { Modal, Typography, Divider, Spin, Empty, Tag } from "antd";
import { FileProtectOutlined, LoadingOutlined } from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

const PrivacyPolicyModal = ({ open, onClose }) => {
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (open) {
      loadPrivacyPolicy();
    }
  }, [open]);

  const loadPrivacyPolicy = () => {
    setLoading(true);
    setError(false);
    const timestamp = new Date().getTime();

    fetch(`/static/config/privacyPolicy.json?t=${timestamp}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setPolicyData(data);
        setLoading(false);
        setError(false);
      })
      .catch(error => {
        console.warn('Could not load privacy policy:', error.message);
        setPolicyData(null);
        setError(true);
        setLoading(false);
      });
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <FileProtectOutlined className="text-blue-600" />
          <span>Privacy Policy</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            tip="Loading privacy policy..."
          />
        </div>
      ) : error || !policyData ? (
        <div className="flex justify-center items-center py-12">
          <Empty
            description={
              <div>
                <Text type="secondary">Privacy policy not available</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Please configure the privacy policy in /public/static/config/privacyPolicy.json
                </Text>
              </div>
            }
          />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Title and Last Updated */}
          <div className="text-center">
            <Title level={3} className="text-blue-600 mb-2">
              {policyData.title}
            </Title>
            <Tag color="blue">Last Updated: {policyData.lastUpdated}</Tag>
          </div>

          <Divider />

          {/* Sections */}
          {policyData.sections?.map((section, index) => (
            <div key={index} className="mb-6">
              <Title level={5} className="text-blue-600 mb-3">
                {section.heading}
              </Title>

              {section.content && (
                <Paragraph className="text-gray-700 mb-3">
                  {section.content}
                </Paragraph>
              )}

              {section.list && section.list.length > 0 && (
                <ul className="list-disc pl-6 space-y-2">
                  {section.list.map((item, idx) => (
                    <li key={idx} className="text-gray-600">
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* Footer Note */}
          <Divider />
          <div className="text-center">
            <Text type="secondary" style={{ fontSize: '12px' }}>
              By using this system, you acknowledge that you have read and understood this Privacy Policy
            </Text>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default PrivacyPolicyModal;
